import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockStrategyProjects } from './mockData';
import { useStateContext } from '../../../contexts/ContextProvider';
import { PageHeader, Modal, Icon, Toolbar, Segmented } from '../../../components/ds';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import StrategyService from '../../../api/services/strategies';
import { services } from '../../../services/api';

const FILTERS = [
    { id: 'ALL', label: 'All Projects' },
    { id: 'LIVE', label: 'Live Trading' },
    { id: 'PAPER', label: 'Paper Trading' },
    { id: 'BACKTESTED', label: 'Backtested' },
    { id: 'STOPPED', label: 'Stopped' },
    { id: 'DRAFT', label: 'Drafts' },
    { id: 'ARCHIVED', label: 'Archived' }
];

const STATUS_LABEL = {
    LIVE: 'Live',
    PAPER: 'Paper',
    BACKTESTED: 'Backtested',
    STOPPED: 'Stopped',
    DRAFT: 'Draft',
    ARCHIVED: 'Archived',
};

const timeAgo = (date) => {
    if (!date) return 'Unknown';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " minutes ago";
    return Math.floor(seconds) + " seconds ago";
};

// DS-tokened kebab dropdown — replaces the MUI <Menu> (which hardcoded a #20232a paper).
// `items`: [{ key, label, icon, onClick, danger }]. Handles click-outside + stopPropagation
// so the row's own onClick (navigate to studio) never fires from a menu interaction.
const KebabMenu = ({ items, buttonClassName, iconSize = 18 }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                className={buttonClassName}
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                title="More options"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Icon name="more" size={iconSize} />
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 z-30 min-w-[150px] py-1 rounded-lg bg-bg-2 border border-[var(--divider)] shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                >
                    {items.map(item => (
                        <button
                            key={item.key}
                            type="button"
                            role="menuitem"
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-bg-3 ${item.danger ? 'text-loss' : 'text-fg-1'}`}
                            onClick={(e) => { e.stopPropagation(); setOpen(false); item.onClick(); }}
                        >
                            <Icon name={item.icon} size={16} className={item.danger ? 'text-loss' : 'text-fg-2'} />
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

//! PERFORMANCE: Use a deterministic, stable ID derived from props instead of Math.random().
//  Math.random() inside a component causes new SVG gradient IDs on every render,
//  forcing browser repaint of all chart SVGs whenever any ancestor state changes.
const MiniChart = ({ color, trend, heightClass = "h-24", projectId = 'default' }) => {
    // Stable gradient ID derived from color + projectId (no random). Sanitize non-alphanumerics
    // so token values like `var(--color-profit)` still yield a valid SVG id.
    const gradientId = `grad-${String(color).replace(/[^a-zA-Z0-9]/g, '')}-${projectId}`;

    // Simple path generation based on trend
    let pathD = "";
    if (trend === "UP") {
        pathD = "M0 35 C 10 32, 20 38, 30 25 C 40 15, 50 20, 60 15 C 70 10, 80 12, 90 5 L 100 2";
    } else if (trend === "DOWN") {
        pathD = "M0 10 C 20 15, 40 10, 60 25 C 70 30, 80 35, 100 38 L 100 40";
    } else {
         // Flat/Backtest
         return (
            <div className={`${heightClass} w-full relative -mx-1 flex items-center justify-center bg-bg-3/30 rounded border border-dashed border-[var(--divider)]`}>
                <div className="text-center px-2 flex flex-col items-center">
                    <Icon name="stats" size={20} className="text-fg-3 mb-1" />
                    <p className="text-[10px] text-fg-3 leading-tight">Backtesting in progress</p>
                </div>
            </div>
         );
    }

    return (
        <div className={`w-full relative -mx-1 ${heightClass}`}>
            <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 40">
                <defs>
                    <linearGradient id={gradientId} x1="0%" x2="0%" y1="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.2 }}></stop>
                        <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }}></stop>
                    </linearGradient>
                </defs>
                <path d={`${pathD} L 100 40 L 0 40 Z`} fill={`url(#${gradientId})`}></path>
                <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke"></path>
            </svg>
        </div>
    );
};

const StrategyCard = ({ project, onOpenLineage, currentColor, onEdit, onDelete }) => {
    const isLive = project.status === "LIVE";
    const isPaper = project.status === "PAPER";
    const isStopped = project.status === "STOPPED";
    const isBacktested = project.status === "BACKTESTED";
    const isDraft = project.status === "DRAFT";

    // Status Styles — dotColor drives the status dot; statusVariant drives the DS badge
    let statusVariant, dotColor;
    if (isLive) {
        statusVariant = "profit";
        dotColor = "bg-[var(--badge-profit-dot)]";
    } else if (isPaper) {
        statusVariant = "info";
        dotColor = "bg-[var(--badge-info-dot)]";
    } else if (isBacktested) {
        statusVariant = "role";
        dotColor = "bg-[var(--badge-role-dot)]";
    } else if (isStopped) {
        statusVariant = "loss";
        dotColor = "bg-[var(--badge-loss-dot)]";
    } else {
        // DRAFT or unknown
        statusVariant = "neutral";
        dotColor = "bg-[var(--badge-warn-dot)]";
    }

    return (
        <div
            className={`bg-bg-2 rounded-2xl p-5 flex flex-col gap-4 shadow-sm card-hover cursor-pointer relative overflow-hidden ${isStopped ? 'opacity-80 hover:opacity-100' : ''}`}
            onClick={(e) => {
                // Prevent routing to lineage instead of studio
                if (onEdit) onEdit(project.id);
            }}
        >
            <div className="flex justify-between items-start z-10">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="ds-h3 text-fg-1">{project.name}</h3>
                        <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium">
                        <span className={`ds-badge xs ${statusVariant}`}>{STATUS_LABEL[project.status] ?? project.status}</span>
                        <span className="text-fg-2 font-mono">{project.symbol}</span>
                         {/* Instrument Type Chip */}
                        {project.instrument_type && project.instrument_type !== 'UNKNOWN' && (
                            <span className="text-[10px] bg-bg-3 px-1 rounded text-fg-2">{project.instrument_type}</span>
                        )}
                        {/* Continuous Badge */}
                        {project.is_continuous && (
                            <span className="text-[10px] px-1 rounded border bg-[var(--badge-info-bg)] text-[var(--badge-info-fg)] border-[var(--badge-info-bg)]" title={`Rollover: ${project.rollover_days} days`}>
                                CONT ({project.rollover_days}d)
                            </span>
                        )}
                        <span className="text-fg-2">•</span>
                        <span className="text-fg-2 font-medium">Primary: {project.version}</span>
                    </div>
                </div>
                <KebabMenu
                    buttonClassName="text-fg-3 hover:text-fg-2 transition-colors"
                    iconSize={18}
                    items={[
                        { key: 'edit', label: 'Edit', icon: 'edit', onClick: () => onEdit && onEdit(project.id) },
                        { key: 'run', label: 'Run', icon: 'play', onClick: () => console.log(`Action run on ${project.name}`) },
                        { key: 'delete', label: 'Delete', icon: 'delete', danger: true, onClick: () => onDelete && onDelete(project) },
                    ]}
                />
            </div>

            {/* Chart Area */}
            <div className="relative">
                <MiniChart color={project.chartColor} trend={project.trend} projectId={project.id} />
                {project.trend !== "FLAT" && project.metrics.pnlPercent != null && (
                    <div className={`absolute bottom-0 right-0 text-xs font-bold ${project.metrics.pnlPercent >= 0 ? 'text-profit' : 'text-loss'} bg-bg-3/80 px-1.5 rounded backdrop-blur-sm`}>
                        {project.metrics.pnlPercent > 0 ? '+' : ''}{project.metrics.pnlPercent}%
                    </div>
                )}
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-2 py-2 border-t border-dashed border-[var(--divider)]">
                <div>
                    <p className="ds-eyebrow text-fg-3">Sharpe</p>
                    <p className={`text-sm font-mono font-bold ${project.metrics.sharpeRatio == null ? 'text-fg-3' : project.metrics.sharpeRatio >= 1 ? 'text-profit' : project.metrics.sharpeRatio >= 0 ? 'text-warning' : 'text-loss'}`}>
                        {project.metrics.sharpeRatio != null ? project.metrics.sharpeRatio : '--'}
                    </p>
                </div>
                <div>
                    <p className="ds-eyebrow text-fg-3">Drawdown</p>
                    <p className={`text-sm font-mono font-bold ${project.metrics.maxDrawdown == null ? 'text-fg-3' : project.metrics.maxDrawdown > 20 ? 'text-loss' : project.metrics.maxDrawdown > 10 ? 'text-warning' : 'text-profit'}`}>
                        {project.metrics.maxDrawdown != null ? `-${project.metrics.maxDrawdown}%` : '--'}
                    </p>
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-1">
                {/* Avatar Placeholder or Time info */}
                 <p className="text-[10px] text-fg-3 italic">
                    {isStopped ? `Last run: ${timeAgo(project.updated_at)}` : `Updated ${timeAgo(project.updated_at)}`}
                </p>

                <button
                    type="button"
                    className="btn btn-secondary btn-sm group"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenLineage(project.id);
                    }}
                >
                    <Icon name="tree" size={16} className="group-hover:rotate-180 transition-transform duration-500" />
                    Version Tree
                </button>
            </div>
        </div>
    );
};

const NewStrategyCard = ({ onClick, currentColor }) => {
    return (
        <div
            className="rounded-2xl border-2 border-dashed border-[var(--divider)] hover:border-[var(--divider)] dark:hover:border-[var(--divider)] bg-transparent p-5 flex flex-col gap-4 items-center justify-center cursor-pointer transition-all duration-300 min-h-[280px] group"
            onClick={onClick}
        >
            <div 
                className="size-16 rounded-full flex items-center justify-center text-white transition-all duration-300 w-16 h-16 shadow-lg group-hover:scale-110"
                style={{ backgroundColor: currentColor }}
            >
                <Icon name="new" size={32} />
            </div>
            <div className="text-center">
                <h3 className="ds-h3 text-fg-1 mb-1">Create Strategy</h3>
                <p className="text-sm text-fg-2">Start from scratch or use a template</p>
            </div>
        </div>
    );
};

const StrategyListItem = ({ project, onOpenLineage, currentColor, onEdit, onDelete }) => {
    const isLive = project.status === "LIVE";
    const isPaper = project.status === "PAPER";
    const isStopped = project.status === "STOPPED";
    const isBacktested = project.status === "BACKTESTED";

    // Status Colors — dotColor drives the status dot; statusVariant drives the DS badge
    let statusVariant, dotColor;
    if (isLive) {
        statusVariant = "profit";
        dotColor = "bg-[var(--badge-profit-dot)]";
    } else if (isPaper) {
        statusVariant = "info";
        dotColor = "bg-[var(--badge-info-dot)]";
    } else if (isBacktested) {
        statusVariant = "role";
        dotColor = "bg-[var(--badge-role-dot)]";
    } else if (isStopped) {
        statusVariant = "loss";
        dotColor = "bg-[var(--badge-loss-dot)]";
    } else {
        // DRAFT
        statusVariant = "neutral";
        dotColor = "bg-[var(--badge-warn-dot)]";
    }

    return (
        <div 
            className="group flex flex-col md:flex-row items-center gap-4 md:gap-0 p-4 bg-bg-2 rounded-xl border border-transparent hover:border-[var(--divider)] dark:hover:border-[var(--divider)] shadow-sm hover:shadow-md transition-all cursor-pointer h-auto md:h-20"
            onClick={(e) => {
                if (onEdit) onEdit(project.id);
            }}
        >
            {/* Strategy & Instrument */}
            <div className="flex items-center gap-4 w-full md:w-[25%] min-w-[200px]">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`}></div>
                <div>
                    <h4 className="font-bold text-fg-1 truncate text-sm">{project.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className={`ds-badge xs ${statusVariant}`}>{STATUS_LABEL[project.status] ?? project.status}</span>
                        <span className="text-xs text-fg-2 font-mono">{project.symbol}</span>
                    </div>
                </div>
            </div>

            {/* Equity Curve (Mini) */}
            <div className="hidden md:block w-[15%] min-w-[120px] h-full py-2 pr-4">
                <div className="h-full w-28 opacity-70 group-hover:opacity-100 transition-opacity">
                    <MiniChart color={project.chartColor} trend={project.trend} heightClass="h-full" projectId={project.id} />
                </div>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-3 gap-4 w-full md:w-[35%] items-center">
                <div>
                    <p className={`font-bold text-sm font-mono ${project.metrics.pnlPercent == null ? 'text-fg-3' : project.metrics.pnlPercent >= 0 ? 'text-profit' : 'text-loss'}`}>
                        {project.metrics.pnlPercent != null ? `${project.metrics.pnlPercent > 0 ? '+' : ''}${project.metrics.pnlPercent}%` : '--'}
                    </p>
                </div>
                <div>
                    <p className={`font-mono text-sm font-bold ${project.metrics.sharpeRatio == null ? 'text-fg-3' : project.metrics.sharpeRatio >= 1 ? 'text-profit' : project.metrics.sharpeRatio >= 0 ? 'text-warning' : 'text-loss'}`}>
                        {project.metrics.sharpeRatio != null ? project.metrics.sharpeRatio : '--'}
                    </p>
                </div>
                <div>
                    <p className={`font-mono text-sm font-bold ${project.metrics.maxDrawdown == null ? 'text-fg-3' : project.metrics.maxDrawdown > 20 ? 'text-loss' : project.metrics.maxDrawdown > 10 ? 'text-warning' : 'text-profit'}`}>
                        {project.metrics.maxDrawdown != null ? `-${project.metrics.maxDrawdown}%` : '--'}
                    </p>
                </div>
            </div>

            {/* Version */}
            <div className="hidden md:block w-[15%] text-right md:text-left">
                <span className="text-xs font-mono text-fg-2 bg-bg-3 px-2 py-1 rounded">
                    Primary: {project.version}
                </span>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 w-full md:w-auto ml-auto">
                <button
                    className="p-2 rounded-lg hover:bg-bg-3 text-fg-3 hover:text-fg-1 transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenLineage(project.id);
                    }}
                    title="Version Tree"
                >
                    <Icon name="tree" size={20} />
                </button>
                <KebabMenu
                    buttonClassName="p-2 rounded-lg hover:bg-bg-3 text-fg-3 hover:text-fg-1 transition-colors"
                    iconSize={20}
                    items={[
                        { key: 'edit', label: 'Edit', icon: 'edit', onClick: () => onEdit && onEdit(project.id) },
                        { key: 'delete', label: 'Delete', icon: 'delete', danger: true, onClick: () => onDelete && onDelete(project) },
                    ]}
                />
            </div>
        </div>
    );
};

const StrategyHub = () => {
    const navigate = useNavigate();
    const { currentColor } = useStateContext();
    const { showSnackbar } = useSnackbar();
    const [viewMode, setViewMode] = useState('grid');
    const [activeFilter, setActiveFilter] = useState('ALL');
    const [searchQuery, setSearchQuery] = useState(''); //-> Search input state
    const [realStrategies, setRealStrategies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [strategyToDelete, setStrategyToDelete] = useState(null);

    // Fetch Strategies on Mount
    useEffect(() => {
        const fetchStrategies = async () => {
            try {
                const [strategiesResp, runsResp] = await Promise.all([
                    StrategyService.getStrategies(),
                    services.backtesting.listRuns({ page_size: 100 }).catch(() => null),
                ]);

                // Build strategy_id → latest run metrics map
                const backtestMap = {};
                const runsData = runsResp?.data?.data || runsResp?.data;
                if (runsData?.runs) {
                    for (const run of runsData.runs) {
                        if (run.strategy_id && run.status === 'completed' && !backtestMap[run.strategy_id]) {
                            backtestMap[run.strategy_id] = run.metrics || {};
                        }
                    }
                }

                if (strategiesResp.data && Array.isArray(strategiesResp.data.strategies)) {
                    const mapped = strategiesResp.data.strategies.map(s => {
                        const m = backtestMap[s.strategy_id] || {};
                        const pnlPct = m.total_return_pct ?? null;
                        const hasBt = pnlPct != null;
                        return {
                            id: s.strategy_id,
                            name: s.name,
                            description: s.description || 'No description',
                            tags: s.tags || [],
                            status: (s.status || 'DRAFT').toUpperCase(),
                            symbol: s.instrument_symbol || 'MULTI',
                            instrument_type: s.instrument_type,
                            is_continuous: s.is_continuous,
                            rollover_days: s.rollover_days,
                            version: s.version_number || 'v1.0',
                            updated_at: s.updated_at,
                            chartColor: hasBt ? (pnlPct >= 0 ? 'var(--color-profit)' : 'var(--color-loss)') : 'var(--fg-3)',
                            trend: hasBt ? (pnlPct >= 0 ? 'UP' : 'DOWN') : 'FLAT',
                            metrics: {
                                pnlPercent: pnlPct != null ? Number(pnlPct.toFixed(2)) : null,
                                sharpeRatio: m.sharpe_ratio != null ? Number(Number(m.sharpe_ratio).toFixed(2)) : null,
                                maxDrawdown: m.max_drawdown != null ? Number(Number(m.max_drawdown).toFixed(2)) : null,
                                totalReturn: m.total_return != null ? Number(m.total_return) : null,
                                winRate: m.win_rate != null ? Number(Number(m.win_rate).toFixed(1)) : null,
                                totalTrades: m.total_trades ?? null,
                            },
                            isReal: true
                        };
                    });
                    setRealStrategies(mapped);
                }
            } catch (err) {
                console.error("Failed to fetch strategies", err);
            } finally {
                setLoading(false);
            }
        };

        fetchStrategies();
    }, []);

    // Filter Logic — applies status filter first, then applies search query
    const filteredStrategies = useMemo(() => {
        const base = [...realStrategies, ...mockStrategyProjects]; // Combine real + mock
        
        let statusFiltered;
        if (activeFilter === 'ALL') {
            // Show everything EXCEPT Archived
            statusFiltered = base.filter(s => s.status !== 'ARCHIVED');
        } else if (activeFilter === 'ARCHIVED') {
            // Show ONLY Archived
            statusFiltered = base.filter(s => s.status === 'ARCHIVED');
        } else {
            // Show specific status
            statusFiltered = base.filter(s => s.status === activeFilter);
        }

        // Apply search query (case-insensitive match on name, symbol, or tags)
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return statusFiltered.filter(s =>
                s.name?.toLowerCase().includes(q) ||
                s.symbol?.toLowerCase().includes(q) ||
                (Array.isArray(s.tags) && s.tags.some(t => t.toLowerCase().includes(q)))
            );
        }

        return statusFiltered;
    }, [realStrategies, activeFilter, searchQuery]);

    // Per-filter counts for the Toolbar tab badges (pre-search totals, same
    // status semantics as the filter above).
    const statusCounts = useMemo(() => {
        const base = [...realStrategies, ...mockStrategyProjects];
        const counts = {};
        for (const f of FILTERS) {
            if (f.id === 'ALL') counts[f.id] = base.filter(s => s.status !== 'ARCHIVED').length;
            else if (f.id === 'ARCHIVED') counts[f.id] = base.filter(s => s.status === 'ARCHIVED').length;
            else counts[f.id] = base.filter(s => s.status === f.id).length;
        }
        return counts;
    }, [realStrategies]);

    const handleOpenLineage = (projectId) => {
        navigate(`/Strategies/Modern/lineage/${projectId}`);
    };

    const handleEditStrategy = (projectId) => {
         navigate(`/strategies/edit/${projectId}`);
    };

    const handleDeleteStrategy = async () => {
        if (!strategyToDelete) return;
        
        try {
            await StrategyService.deleteStrategy(strategyToDelete.id);
            
            //--> Remove from local state
            setRealStrategies(prev => prev.filter(s => s.id !== strategyToDelete.id));
            
            showSnackbar('Strategy deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setStrategyToDelete(null);
        } catch (error) {
            showSnackbar('Failed to delete strategy: ' + (error.message || 'Unknown error'), 'error');
            console.error('Delete error:', error);
        }
    };

    const handleCreateNew = () => {
        navigate('/strategies/create');
    };

    return (
        <div className="flex flex-col bg-main-bg dark:bg-main-dark-bg font-display text-fg-2 w-full min-h-screen relative pt-0 px-4 md:px-6 pb-10">
            {/* Standardized page header (common across all pages) */}
            <PageHeader
                title="Strategy Hub"
                subtitle="Manage your algorithmic strategies and version history"
                actions={
                <div className="flex items-center gap-4 w-full md:w-auto justify-end">
                     {/* Search - Functional search with live filtering */}
                     <div className="relative group w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Icon name="search" size={16} className="text-fg-3" />
                        </div>
                        <input
                            className="block w-full h-[32px] pl-10 pr-3 border border-[var(--divider)] rounded-md bg-bg-2 text-[13px] text-fg-2 placeholder-fg-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all shadow-sm"
                            placeholder="Search strategies..."
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ '--tw-ring-color': currentColor }}
                        />
                     </div>

                    <button
                        type="button"
                        className="btn btn-accent"
                        onClick={handleCreateNew}
                    >
                        <Icon name="new" size={18} />
                        <span className="hidden sm:inline">New Strategy</span>
                    </button>
                </div>
                }
            />

            {/* Slot 2 · Controls (DS Toolbar) — status-filter tabs (nav) + the
                view-scoped grid/list toggle (Segmented). Calm neutral pills with
                count badges; the accent stays reserved for the New Strategy CTA. */}
            <Toolbar
                className="mb-6"
                nav={
                    <div className="flex items-center gap-1 overflow-x-auto no-scrollbar w-full">
                        {FILTERS.map(filter => {
                            const isActive = activeFilter === filter.id;
                            return (
                                <button
                                    key={filter.id}
                                    type="button"
                                    onClick={() => setActiveFilter(filter.id)}
                                    className={`flex-shrink-0 flex items-center h-8 px-3 rounded-md text-[13px] tracking-wide transition-colors whitespace-nowrap ${
                                        isActive
                                            ? 'bg-bg-3 text-fg-1 font-semibold'
                                            : 'text-fg-3 hover:text-fg-1 hover:bg-bg-3 font-medium'
                                    }`}
                                >
                                    {filter.label}
                                    <span
                                        className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold leading-none tabular-nums ${
                                            isActive ? 'text-accent' : 'bg-bg-3 text-fg-2'
                                        }`}
                                        style={isActive ? { background: 'color-mix(in oklch, var(--color-accent) 22%, transparent)' } : undefined}
                                    >
                                        {statusCounts[filter.id] ?? 0}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                }
                filters={
                    <Segmented
                        aria-label="View mode"
                        value={viewMode}
                        onChange={setViewMode}
                        options={[
                            { value: 'grid', label: 'Grid', icon: <Icon name="grid" size={14} /> },
                            { value: 'list', label: 'List', icon: <Icon name="orders" size={14} /> },
                        ]}
                    />
                }
            />

            {/* Content Area */}
            {viewMode === 'grid' ? (
                // Grid View
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 pb-20">
                    {/* Filtered Projects */}
                    {filteredStrategies.map(proj => (
                        <StrategyCard 
                            key={proj.id} 
                            project={proj} 
                            onOpenLineage={handleOpenLineage} 
                            currentColor={currentColor} 
                            onEdit={handleEditStrategy}
                            onDelete={(project) => {
                                setStrategyToDelete(project);
                                setDeleteDialogOpen(true);
                            }}
                        />
                    ))}
                    {activeFilter !== 'ARCHIVED' && (
                        <NewStrategyCard onClick={handleCreateNew} currentColor={currentColor} />
                    )}
                </div>
            ) : (
                // List View - Modern Detached Rows
                <div className="flex flex-col gap-3 pb-20 w-full">
                    {/* List Headers */}
                    <div className="hidden md:flex px-4 py-2 ds-eyebrow text-fg-3 items-center">
                        <div className="w-[25%] min-w-[200px]">Strategy & Instrument</div>
                        <div className="w-[15%] min-w-[120px]">Equity Curve</div>
                        <div className="w-[35%] grid grid-cols-3 gap-4">
                            <div>Performance</div>
                            <div>Sharpe</div>
                            <div>Drawdown</div>
                        </div>
                        <div className="w-[15%] text-left">Version</div>
                        <div className="w-auto ml-auto">Actions</div>
                    </div>

                    {filteredStrategies.map((proj) => (
                        <StrategyListItem 
                            key={proj.id} 
                            project={proj} 
                            onOpenLineage={handleOpenLineage} 
                            currentColor={currentColor} 
                            onEdit={handleEditStrategy}
                            onDelete={(project) => {
                                setStrategyToDelete(project);
                                setDeleteDialogOpen(true);
                            }}
                        />
                    ))}
                    
                    <button 
                        className="mt-4 py-4 border-2 border-dashed border-[var(--divider)] rounded-xl flex items-center justify-center gap-2 text-fg-2 hover:border-[var(--divider)] dark:hover:border-[var(--divider)] hover:text-fg-3 transition-all font-bold uppercase tracking-wide text-xs group"
                        onClick={handleCreateNew}
                    >
                        <Icon name="new" size={18} />
                        Create New Strategy
                    </button>
                </div>
            )}
            
            {/* Delete Confirmation Dialog */}
            <Modal
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                size="md"
                title="Delete Strategy?"
                footer={
                    <>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={handleDeleteStrategy}
                            autoFocus
                        >
                            Delete
                        </button>
                    </>
                }
            >
                <p className="ds-body">
                    Are you sure you want to delete <strong>"{strategyToDelete?.name}"</strong>?
                    This action cannot be undone.
                </p>
            </Modal>
        </div>
    );
};

export default StrategyHub;
